import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '');
}

export default function References() {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadReferences() {
      setLoading(true);
      try {
        const data = await fetchCollection('references');
        setReferences(data);
      } catch (error) {
        console.error('Error loading references:', error);
      } finally {
        setLoading(false);
      }
    }
    loadReferences();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-solid fa-spinner-third animate-spin text-4xl text-accent" />
          <p className="text-slate-500 font-medium">Loading references...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[73px]">
      {/* Header Section */}
      <section className="bg-primary text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/img-0230.jpg"
            alt="References Background"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 text-center space-y-4">
          <span className="text-accent text-xs font-bold tracking-widest uppercase">Our Projects</span>
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold tracking-tight text-white">
            Proven Track Record Worldwide
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
            At Site Facility, we conduct meticulous research to identify the ideal solution that aligns with your specific needs and budget. Have a look at our references and projects.
          </p>
        </div>
      </section>

      {/* References Grid */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {references.map((ref, idx) => (
              <div 
                key={ref.id} 
                className={`bg-slate-50/50 rounded-2xl border border-slate-100/80 overflow-hidden flex flex-col justify-between hover:bg-slate-50 hover:border-accent/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${
                  idx === references.length - 1 && references.length % 2 !== 0 ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
              >
                <div>
                  {/* Image */}
                  <div className="h-56 overflow-hidden relative">
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${ref.images[0]}`}
                      alt={ref.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs font-bold text-primary shadow-sm">
                      {ref.duration}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-accent uppercase tracking-wider">Client: {ref.client}</span>
                    </div>
                    <h3 className="text-xl font-bold text-primary">
                      {ref.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed line-clamp-3">
                      {stripHtml(ref.content)}
                    </p>
                  </div>
                </div>

                <div className="p-6 pt-0">
                  <Link
                    to={`/references/${ref.slug.en}`}
                    className="inline-flex items-center gap-2 py-2.5 px-4 rounded-xl bg-slate-50 text-primary font-semibold hover:bg-accent hover:text-white transition-all-300 w-full justify-center border border-slate-100 text-sm"
                  >
                    <span>View Project Details</span>
                    <i className="fa-regular fa-arrow-right text-xs" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="mx-auto max-w-5xl px-6 text-center space-y-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-primary">
            Have a Project in Mind?
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Let us help you design, plan, and execute a high-quality, sustainable accommodation and logistics setup for your remote workforce.
          </p>
          <div className="pt-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 py-4 px-8 rounded-xl bg-primary text-white font-semibold hover:bg-primary-light transition-all-300 shadow-sm"
            >
              <span>Contact Our Specialists</span>
              <i className="fa-regular fa-paper-plane text-sm" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
